// Thẻ "Gửi từ" trên form tạo đơn: địa chỉ gửi hàng (= kho lấy hàng) đang dùng cho đơn này, nút
// "Đổi" mở hộp chọn, "Thêm địa chỉ mới" thêm ngay trong hộp (không rời form → không mất đơn đang
// nhập). Đơn mới luôn bắt đầu từ địa chỉ mặc định — muốn đổi lâu dài thì tích "Dùng làm địa chỉ mặc định".

import { useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MapPin, Plus } from 'lucide-react'
import { post } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { formatPhoneVN } from '@/lib/format'
import { invalidateProfileStatus } from '@/lib/onboarding'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  WarehouseFormFields,
  emptyWarehouseForm,
  fullAddress,
  warehousePayload,
  type Warehouse,
  type WarehouseFormValue,
} from '@/components/settings/WarehouseForm'

export interface SenderAddress {
  id: number
  name: string
  contactName: string
  contactPhone: string
  address: string
  city: string
  district: string | null
  ward: string
  isDefault: boolean
}

const line = (w: SenderAddress) => fullAddress(w.address ?? '', w.ward ?? '', w.district, w.city ?? '')

export function SenderAddressCard({
  addresses,
  selected,
  loading,
  onSelect,
  className,
  children,
  canAdd = true,
}: {
  addresses: SenderAddress[]
  selected: SenderAddress | undefined
  loading: boolean
  onSelect: (id: number) => void
  className?: string
  /** Thêm nội dung trong thẻ, dưới địa chỉ (vd. hình thức lấy hàng ở form tạo đơn) */
  children?: ReactNode
  /** false = không có nút thêm địa chỉ gửi (nhân viên không có quyền kho lấy hàng) */
  canAdd?: boolean
}) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [picked, setPicked] = useState<number | null>(null)
  const auth = getAuthState()
  const shopName = auth.shops.find((s) => s.shopId === auth.activeShopId)?.name || ''
  const [form, setForm] = useState<WarehouseFormValue>(() => emptyWarehouseForm(shopName, auth.phone || '', true, 'sender'))

  const openPicker = () => {
    setPicked(selected?.id ?? null)
    setAdding(false)
    setOpen(true)
  }
  const openAdd = () => {
    setForm(emptyWarehouseForm(shopName, auth.phone || '', addresses.length === 0, 'sender'))
    setAdding(true)
    setOpen(true)
  }

  const save = useMutation({
    mutationFn: () => post<{ warehouse: Warehouse }>('/settings/warehouses', warehousePayload(form, 'sender')),
    onSuccess: ({ warehouse }) => {
      // Có ngay trong danh sách để chọn được liền, rồi tải lại (cờ mặc định của các địa chỉ khác)
      qc.setQueryData<{ warehouses: SenderAddress[] }>(['warehouses'], (old) => ({
        warehouses: [...(old?.warehouses ?? []).map((w) => (warehouse.isDefault ? { ...w, isDefault: false } : w)), warehouse],
      }))
      void qc.invalidateQueries({ queryKey: ['warehouses'] })
      void invalidateProfileStatus()
      onSelect(warehouse.id)
      toast.success(`Đã lưu "${warehouse.name}" và dùng cho đơn này`)
      setAdding(false)
      setOpen(false)
    },
    onError: (e) => toast.error((e as Error).message),
  })

  return (
    <>
      <div className={cn("flex flex-col gap-2.5 rounded-xl border border-rule bg-raise p-3 shadow-card", className)}>
       <div className="flex items-center gap-2.5">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
          <MapPin className="size-4" />
        </div>
        <div className="min-w-0 flex-1">
          {loading ? (
            <p className="text-sm text-muted">Đang tải…</p>
          ) : selected ? (
            <>
              <p className="truncate text-sm text-ink">
                <span className="text-xs text-muted">Gửi từ </span>
                <span className="font-semibold">{selected.name}</span>
                {selected.isDefault && <span className="ml-1 text-xs font-medium text-accent">(mặc định)</span>}
              </p>
              <p className="text-xs text-inkSoft truncate">
                {line(selected)} · {formatPhoneVN(selected.contactPhone)}
              </p>
            </>
          ) : (
            <p className="text-sm font-medium text-danger">Gửi từ: chưa có địa chỉ gửi hàng</p>
          )}
        </div>
        {!loading &&
          (selected ? (
            <Button type="button" variant="outline" size="sm" className="shrink-0 h-10 desktop:h-8" onClick={openPicker}>
              Đổi
            </Button>
          ) : canAdd ? (
            <Button type="button" size="sm" className="shrink-0 h-10 desktop:h-8 gap-1" onClick={openAdd}>
              <Plus className="size-3.5" /> Thêm địa chỉ gửi
            </Button>
          ) : null)}
       </div>
        {children}
      </div>

      <Dialog open={open} onOpenChange={(v) => !save.isPending && setOpen(v)}>
        <DialogContent className="sm:max-w-2xl max-h-[92dvh] overflow-y-auto" aria-describedby={undefined}>
          {adding ? (
            <>
              <DialogHeader>
                <DialogTitle>Thêm địa chỉ gửi</DialogTitle>
              </DialogHeader>
              <WarehouseFormFields form={form} setForm={setForm} disabled={save.isPending} showDefault={addresses.length > 0} wording="sender" />
              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 desktop:h-9"
                  disabled={save.isPending}
                  onClick={() => (addresses.length > 0 ? setAdding(false) : setOpen(false))}
                >
                  {addresses.length > 0 ? 'Quay lại' : 'Hủy'}
                </Button>
                <Button type="button" className="h-11 desktop:h-9" disabled={save.isPending} onClick={() => save.mutate()}>
                  {save.isPending ? 'Đang lưu…' : 'Lưu và dùng'}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Chọn địa chỉ gửi hàng</DialogTitle>
              </DialogHeader>
              <div role="radiogroup" aria-label="Địa chỉ gửi hàng" className="space-y-2">
                {addresses.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    role="radio"
                    aria-checked={picked === w.id}
                    onClick={() => setPicked(w.id)}
                    className={cn(
                      'w-full rounded-lg border p-3 text-left transition-colors cursor-pointer',
                      picked === w.id ? 'border-accent bg-accent/5 ring-2 ring-accent/20' : 'border-rule hover:bg-paperSoft',
                    )}
                  >
                    <p className="text-sm font-semibold text-ink">
                      {w.name}
                      {w.isDefault && <span className="ml-1.5 rounded bg-accent/12 px-1.5 py-0.5 text-2xs font-medium text-accent">Mặc định</span>}
                    </p>
                    <p className="text-xs text-inkSoft">
                      {line(w)} · {w.contactName} · {formatPhoneVN(w.contactPhone)}
                    </p>
                  </button>
                ))}
              </div>
              {canAdd && (
                <Button type="button" variant="ghost" className="h-11 desktop:h-9 w-full justify-start gap-1.5 text-accent" onClick={openAdd}>
                  <Plus className="size-4" /> Thêm địa chỉ gửi
                </Button>
              )}
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" className="h-11 desktop:h-9" onClick={() => setOpen(false)}>
                  Hủy
                </Button>
                <Button
                  type="button"
                  className="h-11 desktop:h-9"
                  disabled={picked == null}
                  onClick={() => {
                    if (picked != null) onSelect(picked)
                    setOpen(false)
                  }}
                >
                  Dùng địa chỉ này
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
